import React from "react";
//#region types
import { WeekSummaryProps } from "models/propTypes/timeReportingPropTypes";
import { DateConstants, IconDefinitionConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { TimeReportingEnums } from "models/enums";
import { TimeReportingFormTypes } from "models/formTypes";
//#endregion
//#region components
import { Divider, Grid } from "@mui/material";
import { Icon, ViewPanel } from "components/common";
import { useSalaryPermissions } from "components/salary";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import TimeReportHelperInstance from "components/timeReporting/timeReportHelper";

interface ProjectSummary {
  rows: TimeReportingFormTypes.WeeklyTimeReportRow[];
  name: string;
  id?: string;
}

interface DaySummary {
  day: string;
  dayIndex: number;
  catagorySummeries: string[];
}

const WeekSummary: React.FC<WeekSummaryProps> = (props) => {
  const { formData, weekStart } = props;

  const [projectTotal, setProjectTotal] = React.useState<number>(0);
  const [deviationTotal, setDeviationTotal] = React.useState<number>(0);
  const { isSalaryDeviationsAvailable } = useSalaryPermissions();

  const projectSummary = React.useMemo(() => {
    let allProjectTotal = 0;

    const groupedProjects: ProjectSummary[] = [];
    formData
      ?.filter((row) => row.type === TimeReportingEnums.TimeReportType.project)
      .forEach((row) => {
        let project = groupedProjects.find((proj) => proj.id === row.projectId);
        if (!project) {
          project = { id: row.projectId, rows: [], name: row.name };
          groupedProjects.push(project);
        }

        project.rows.push(row);
      });

    const projectData = groupedProjects.map((proj) => {
      let projectHours = 0;
      const categoriesTotal: string[] = [];
      let daySummaries: DaySummary[] = [];

      proj.rows.forEach((row) => {
        let categoryHours = 0;
        DateConstants.FullWeekStartWithMonday.forEach((dayOfWeek, index) => {
          const hours = Number((row as any)[dayOfWeek]) ?? 0;
          if (hours > 0) {
            categoryHours += hours;
            const projDate = weekStart && DateHelper.addDays(weekStart, index);
            const day = `${LocalizationHelperInstance.getFormattedMessage(
              `TimeReporting.WeekDayShortFromMonday_${index}`
            )}, ${projDate?.getDate()} ${
              projDate && LocalizationHelperInstance.getFormattedDate(projDate, DateConstants.MonthShortConfig)
            }`;

            let daySummary = daySummaries.find((summary) => summary.day === day);

            if (!daySummary) {
              daySummary = { day: day, dayIndex: index, catagorySummeries: [] };
              daySummaries.push(daySummary);
            }

            daySummary.catagorySummeries.push(`${TimeReportHelperInstance.formatHours(hours)}h ${row.categoryName}`);
          }
        });

        if (categoryHours > 0) {
          projectHours += categoryHours;
          categoriesTotal.push(`${TimeReportHelperInstance.formatHours(categoryHours)}h ${row.categoryName}`);
        }
      });

      allProjectTotal += projectHours;
      daySummaries = daySummaries.sort((a, b) => a.dayIndex - b.dayIndex);
      return { name: proj.name, total: projectHours, categoriesTotal: categoriesTotal, days: daySummaries };
    });

    setProjectTotal(allProjectTotal);
    return projectData;
  }, [formData]);

  const deviationSummary = React.useMemo(() => {
    let allDeviationTotal = 0;
    const deviationRows = formData?.filter((row) => row.type === TimeReportingEnums.TimeReportType.deviation);
    const deviationData = DateConstants.FullWeekStartWithMonday.map((day, index) => {
      const projDate = weekStart && DateHelper.addDays(weekStart, index);
      return deviationRows
        ?.map((dr) => {
          const hours = Number((dr as any)[day]) ?? 0;
          if (hours > 0) {
            allDeviationTotal += hours;
            return `${TimeReportHelperInstance.formatHours(hours)}h ${LocalizationHelperInstance.getFormattedMessage(
              `TimeReporting.WeekDayShortFromMonday_${index}`
            )}, ${projDate?.getDate()} ${
              projDate && LocalizationHelperInstance.getFormattedDate(projDate, DateConstants.MonthShortConfig)
            }, ${LocalizationHelperInstance.getFormattedMessage(`SalaryDeviation.Type_${dr.deviationType}`)} ${
              dr.childName ? `(${dr.childName})` : ""
            }`;
          } else {
            return undefined;
          }
        })
        .filter((dr) => dr !== undefined);
    }).flatMap((d) => d);
    setDeviationTotal(allDeviationTotal);
    return deviationData;
  }, [formData]);

  const getTotal = () => TimeReportHelperInstance.formatHours(projectTotal + deviationTotal);

  return (
    <Grid className={cssExports.summaryContainer}>
      <ViewPanel>
        <Grid container direction={"column"} spacing={2}>
          <Grid item container>
            <Grid className={cssExports.viewPanelIcon}>
              <Icon icon={IconDefinitionConstants.WTimer} />
            </Grid>
            <span>{`${getTotal()} h`}</span>
          </Grid>

          <Grid item>
            <Divider className={cssExports.dividerGray} />
          </Grid>
          {projectSummary && (
            <Grid item container spacing={2}>
              <Grid item container alignItems={"center"}>
                <Grid item className={cssExports.viewPanelIcon}>
                  <Icon icon={IconDefinitionConstants.WFolder} />
                </Grid>
                <Grid item>{LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.Project)}</Grid>
              </Grid>
              {projectSummary?.length ? (
                <Grid item container>
                  <Grid spacing={2} container direction={"column"}>
                    {projectSummary.map((proj) => (
                      <Grid container item key={`project-${proj.name}`} spacing={1}>
                        <Grid item container direction={"column"}>
                          <Grid item>{`${proj.name} (${TimeReportHelperInstance.formatHours(proj.total)}h)`}</Grid>
                          <Grid item className={cssExports.summaryGroupContent}>
                            {`${proj.categoriesTotal.join(", ")}`}
                          </Grid>
                        </Grid>
                        <Grid item container>
                          {proj.days.map((daySummary) => (
                            <Grid item container key={`project-day-${daySummary.day}`}>
                              <Grid item xs={4}>
                                {daySummary.day}
                              </Grid>
                              <Grid item className={cssExports.summaryGroupContent}>
                                {daySummary.catagorySummeries.join(", ")}
                              </Grid>
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              ) : (
                <Grid item container className={cssExports.summaryGroupPlaceholder}>
                  {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.ProjectPlaceholder)}
                </Grid>
              )}
            </Grid>
          )}
          {isSalaryDeviationsAvailable && (
            <Grid item>
              <Divider className={cssExports.dividerGray} />
            </Grid>
          )}
          {isSalaryDeviationsAvailable && deviationSummary && (
            <Grid item container spacing={2}>
              <Grid item container>
                <Grid item className={cssExports.viewPanelIcon}>
                  <Icon icon={IconDefinitionConstants.WCalendarTime} />
                </Grid>
                <Grid item>{LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Deviations)} </Grid>
              </Grid>

              {deviationSummary?.length ? (
                <Grid item>
                  {deviationSummary.map((dev, index) => (
                    <Grid item xs key={`deviation-${index}`}>
                      {dev}
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid item container className={cssExports.summaryGroupPlaceholder}>
                  {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.DeviationsPlaceholder)}
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
      </ViewPanel>
    </Grid>
  );
};

export default WeekSummary;
